<template>
  <div>
    <div
      :class="[
        { '--root': !subpage },
        'nav-page-node d-flex align-items-center'
      ]"
      data-cy="nav-page"
    >
      <v-button
        v-if="page.children.length"
        round
        ghost
        small
        secondary
        class="nav-page-node__expand"
        data-cy="nav-page-expand"
        @click="() => (isExpanded = !isExpanded)"
      >
        <v-icon
          :icon="isExpanded ? 'expand_more' : 'chevron_right'"
          size="18px"
        />
      </v-button>

      <v-link
        :to="page.path"
        :page-id="page.id"
        with-access-checks
        :replace="!isDesktop"
      >
        <template v-slot="{ isLocked, isLoadingAccess, availableAfter }">
          <v-icon
            class="nav-page-node__icon"
            :icon="page.icon || 'description'"
          />
          <span>
            {{ page.title }}
          </span>
          <template v-if="isLocked || isLoadingAccess">
            <access-lock
              v-if="isLocked && !isLoadingAccess"
              horizontal
              :is-loading="isLoadingAccess"
              :available-after="availableAfter"
            />
          </template>
          <template v-else-if="page.is_completable">
            <complete-check
              class="nav-page-node__complete"
              horizontal
              :complete="isPageCompleted"
            />
          </template>
        </template>
      </v-link>
    </div>

    <template v-if="isExpanded">
      <div class="nav-page-node__wrapper">
        <template v-if="page.pages.length">
          <navigation-page-node
            v-for="page in page.pages"
            :key="page.id"
            :page="page"
            subpage
          />
        </template>
        <template v-else-if="page.children">
          <navigation-page-container
            v-for="child in page.children"
            :key="child"
            :root-id="child"
            subpage
          />
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex"

export default {
  name: "NavigationPageNode",

  components: {
    NavigationPageContainer: () =>
      import("@/components/NavigationPageContainer"),
    AccessLock: () => import("@/components/AccessLock"),
    CompleteCheck: () => import("@/components/collection-item/CompleteCheck")
  },

  props: {
    page: { type: Object, required: true },
    subpage: { type: Boolean, default: false }
  },

  computed: {
    ...mapGetters("pages", ["isPageComplete"]),
    ...mapState("responsive", ["isDesktop"]),

    isPageCompleted() {
      return this.isPageComplete(this.page.id)
    }
  },

  data() {
    return {
      isExpanded: !this.subpage
    }
  },

  watch: {
    $route: {
      handler() {
        if (
          this.$route.path === this.page.path ||
          this.$route.path.startsWith(this.page.path + "/")
        ) {
          this.isExpanded = true
        } else if (this.subpage) {
          this.isExpanded = false
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.nav-page-node {
  position: relative;
  animation: fade-in 0.2s ease;

  .v-link {
    display: flex;
    align-items: center;
    color: $color-ui--grey-70;
    padding: $size--2;
    padding-right: $size--5;
    text-decoration: none;
    text-transform: none;
    width: calc(100% - 1rem);
    margin-left: 0.5rem;
    margin-bottom: 0.25rem;
    border-radius: var(--border-radius);
    font-weight: $font-weight--base;

    transition: color 0.2s ease, background 0.2s ease;

    &.--active {
      color: var(--primary-brand-color-hex);
      font-weight: $font-weight--semi-bold;
      background: $color-ui--grey-10;
    }

    span:not(.v-icon) {
      font-size: $font-size--small;

      @include overflow-ellipsis;
    }

    &:hover {
      color: var(--primary-brand-color-hex);
      background: $color-ui--grey-15;
    }
  }

  .nav-page-node__icon {
    transition: color 0.2s ease, background 0.2s ease;
    font-size: 1.1rem;
    color: $color-ui--grey-80;
    margin-right: $size--2;
  }

  .nav-page-node__complete.collection-item__complete-check {
    top: 7px !important;
    left: 14px;
    margin-top: 0;
    display: none;
    height: 1.25rem;
    width: 1.25rem;

    &.--complete {
      display: flex;
    }
  }

  &.--root {
    margin-top: 1rem;

    .v-link {
      padding-left: 0.4rem;
      font-weight: $font-weight--semi-bold;
      color: $color-ui--grey-90;

      &.--active {
        font-weight: $font-weight--bold;
        background: $color-ui--grey-10;
        color: var(--primary-brand-color-hex);

        .v-icon {
          color: var(--primary-brand-color-hex);
        }
      }
    }

    .nav-page-node__icon {
      font-size: $font-size--h2;
      color: $color-ui--grey-70;
      margin-right: $size--2;
    }

    .v-button.nav-page-node__expand {
      top: 8px;
      right: 14px;
    }

    &:hover {
      .v-link,
      .nav-page-node__icon {
        color: $color-ui--grey-90;
      }
    }
  }

  .access-lock.--horizontal {
    height: 1.5rem;
    width: 1.5rem;
    top: 5px !important;
    margin-top: 0;
    right: 14px !important;
  }
}

.v-button.--small.nav-page-node__expand {
  position: absolute;
  right: 12px;
  top: 4px;
  padding: 8px;
  height: 1.5rem;
  min-height: 1.5rem;
  max-height: 1.5rem;
  width: 1.5rem;
  min-width: 1.5rem;
  max-width: 1.5rem;
  border-radius: var(--border-radius);
  color: $color-ui--grey-70;

  &:hover {
    background: $color-ui--grey-20;
  }
}

.nav-page-node__wrapper {
  .nav-page-container {
    padding-left: 0.5rem;
  }
}
</style>
